import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  FaUserMd,
  FaUserInjured,
  FaSearch,
  FaCalendarCheck,
  FaHeartbeat,
  FaShieldAlt,
  FaArrowRight,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaAmbulance,
  FaFlask,
  FaCapsules,
  FaLeaf,
  FaHome,
  FaUserPlus,
  FaSignInAlt,
  FaChevronDown,
  FaStethoscope,
} from "react-icons/fa";

import {
  MdLocalHospital,
  MdHealthAndSafety,
} from "react-icons/md";

import SearchBar from "../../src/Components/SearchBar/SearchBar";

/*
|--------------------------------------------------------------------------
| MRHEALTH COLORS
|--------------------------------------------------------------------------
*/

const CUSTOM_GRADIENT =
  "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)";

const BUTTON_GRADIENT =
  "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))";

const PRIMARY_DARK = "rgb(15, 31, 51)";
const PRIMARY = "rgb(26, 58, 92)";
const PRIMARY_LIGHT = "rgb(30, 73, 118)";
const SKY = "rgb(14, 165, 233)";

/*
|--------------------------------------------------------------------------
| LANDING PAGE
|--------------------------------------------------------------------------
*/

const LandingPage = () => {
  const navigate = useNavigate();

  const [showSignupMenu, setShowSignupMenu] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | SIGNUP NAVIGATION
  |--------------------------------------------------------------------------
  */

  const goToPatientSignup = () => {
    setShowSignupMenu(false);
    navigate("/sign-up");
  };

  const goToDoctorSignup = () => {
    setShowSignupMenu(false);
    navigate("/RegistrationForm");
  };

  /*
  |--------------------------------------------------------------------------
  | HEALTHCARE SERVICES
  |--------------------------------------------------------------------------
  */

  const specialties = [
    {
      title: "Doctors",
      description:
        "Find trusted doctors and healthcare specialists.",
      icon: <FaUserMd />,
    },
    {
      title: "Hospitals",
      description:
        "Discover hospitals and healthcare facilities.",
      icon: <MdLocalHospital />,
    },
    {
      title: "Diagnostic Centers",
      description:
        "Find diagnostic labs and healthcare testing services.",
      icon: <FaFlask />,
    },
    {
      title: "Medical Shops",
      description:
        "Locate medical stores and pharmacy services.",
      icon: <FaCapsules />,
    },
    {
      title: "Ambulances",
      description:
        "Find ambulance and emergency healthcare services.",
      icon: <FaAmbulance />,
    },
    {
      title: "Alternative Medicine",
      description:
        "Explore Ayurveda, Homeopathy, Yoga and wellness.",
      icon: <FaLeaf />,
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | FEATURES
  |--------------------------------------------------------------------------
  */

  const features = [
    {
      icon: <FaSearch />,
      title: "Search Healthcare",
      description:
        "Search doctors, hospitals, laboratories, pharmacies and healthcare services.",
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Find Nearby",
      description:
        "Discover healthcare professionals and services based on your location.",
    },
    {
      icon: <FaCalendarCheck />,
      title: "Book Appointments",
      description:
        "Connect with healthcare professionals and manage appointments easily.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure & Reliable",
      description:
        "A simple platform designed to make your healthcare journey easier.",
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | HOW IT WORKS
  |--------------------------------------------------------------------------
  */

  const steps = [
    {
      number: "01",
      icon: <FaSearch />,
      title: "Search",
      text:
        "Search for the healthcare professional or service you need.",
    },
    {
      number: "02",
      icon: <FaUserMd />,
      title: "Choose",
      text:
        "Explore healthcare professionals and services available to you.",
    },
    {
      number: "03",
      icon: <FaCalendarCheck />,
      title: "Connect",
      text:
        "Connect with the right healthcare professional and manage your appointment.",
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-white text-gray-900">

      {/* =================================================================
          NAVBAR
      ================================================================= */}

      <nav
        className="
          sticky top-0 z-50
          w-full
          min-w-0
          border-b border-sky-200/80
          bg-[linear-gradient(135deg,rgba(14,165,233,0.18)_0%,rgba(255,255,255,0.90)_50%,rgba(30,73,118,0.16)_100%)]
          backdrop-blur-md
        "
      >
        <div
          className="
            mx-auto
            flex
            min-h-[64px]
            w-full
            min-w-0
            max-w-screen-2xl
            items-center
            justify-between
            gap-2
            px-2
            sm:min-h-[68px]
            sm:px-4
            md:px-6
            lg:px-10
            xl:px-12
          "
        >

          {/* LOGO */}

          <button
            type="button"
            onClick={() => navigate("/Home")}
            className="
              flex
              min-w-0
              shrink
              items-center
              overflow-hidden
            "
          >
            <img
              src="/logo.jpeg"
              alt="MR Health"
              title="MR Health"
              className="
                h-12
                w-auto
                max-w-[150px]
                object-contain
                sm:h-14
                sm:max-w-[170px]
                md:h-16
                lg:h-18
                xl:h-20
              "
            />
          </button>

          {/* =============================================================
              DESKTOP NAVIGATION
          ============================================================= */}

          <div
            className="
              hidden
              min-w-0
              items-center
              gap-2
              md:flex
              lg:gap-3
            "
          >

            {/* HOME */}

            <Link
              to="/Home"
              className="
                flex
                shrink-0
                items-center
                gap-2
                rounded-xl
                px-3
                py-2
                text-xs
                font-bold
                text-white
                transition
                hover:opacity-90
                lg:px-4
                lg:text-sm
              "
              style={{
                background: BUTTON_GRADIENT,
              }}
            >
              <FaHome />
              <span>Home</span>
            </Link>

            {/* ABOUT */}

            <Link
              to="/about"
              className="
                shrink-0
                px-2
                py-2
                text-xs
                font-semibold
                transition
                lg:text-sm
              "
              style={{
                color: PRIMARY,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = SKY;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = PRIMARY;
              }}
            >
              About
            </Link>

            {/* POLICIES */}

            <Link
              to="/legal-policies"
              className="
                shrink-0
                px-2
                py-2
                text-xs
                font-semibold
                transition
                lg:text-sm
              "
              style={{
                color: PRIMARY,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = SKY;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = PRIMARY;
              }}
            >
              Policies
            </Link>

            {/* LOGIN */}

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="
                flex
                shrink-0
                items-center
                gap-2
                rounded-xl
                px-3
                py-2
                text-xs
                font-bold
                transition
                hover:bg-white/60
                lg:px-4
                lg:text-sm
              "
              style={{
                color: PRIMARY,
              }}
            >
              <FaSignInAlt />
              Login
            </button>

            {/* SIGN UP */}

            <div className="relative shrink-0">

              <button
                type="button"
                onClick={() =>
                  setShowSignupMenu((prev) => !prev)
                }
                className="
                  flex
                  items-center
                  gap-1.5
                  rounded-xl
                  px-3
                  py-2
                  text-xs
                  font-bold
                  text-white
                  shadow-md
                  transition
                  hover:opacity-90
                  lg:gap-2
                  lg:px-5
                  lg:py-2.5
                  lg:text-sm
                "
                style={{
                  background: BUTTON_GRADIENT,
                }}
              >
                <FaUserPlus />

                <span>Sign Up</span>

                <FaChevronDown
                  className={`
                    text-[9px]
                    transition-transform
                    lg:text-xs
                    ${
                      showSignupMenu
                        ? "rotate-180"
                        : ""
                    }
                  `}
                />
              </button>

              {showSignupMenu && (
                <div
                  className="
                    absolute
                    right-0
                    top-full
                    z-[100]
                    mt-2
                    w-[min(16rem,calc(100vw-1rem))]
                    overflow-hidden
                    rounded-2xl
                    border
                    border-gray-100
                    bg-white
                    shadow-2xl
                  "
                >

                  {/* PATIENT */}

                  <button
                    type="button"
                    onClick={goToPatientSignup}
                    className="
                      flex
                      w-full
                      items-center
                      gap-3
                      px-4
                      py-4
                      text-left
                      transition
                      hover:bg-sky-50
                    "
                  >
                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        text-white
                      "
                      style={{
                        background: BUTTON_GRADIENT,
                      }}
                    >
                      <FaUserInjured />
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-gray-800">
                        Patient Sign Up
                      </p>

                      <p className="mt-0.5 text-xs text-gray-500">
                        Create your patient account
                      </p>
                    </div>
                  </button>

                  <div className="mx-4 border-t border-gray-100" />

                  {/* PROFESSIONAL */}

                  <button
                    type="button"
                    onClick={goToDoctorSignup}
                    className="
                      flex
                      w-full
                      items-center
                      gap-3
                      px-4
                      py-4
                      text-left
                      transition
                      hover:bg-sky-50
                    "
                  >
                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-emerald-100
                        text-emerald-600
                      "
                    >
                      <FaUserMd />
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-gray-800">
                        Doctor / Professional
                      </p>

                      <p className="mt-0.5 text-xs text-gray-500">
                        Register your healthcare service
                      </p>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* =============================================================
              MOBILE ACTIONS
          ============================================================= */}

          <div
            className="
              flex
              min-w-0
              shrink-0
              items-center
              gap-1.5
              md:hidden
            "
          >

            {/* LOGIN */}

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="
                flex
                min-h-[38px]
                shrink-0
                items-center
                justify-center
                gap-1.5
                rounded-xl
                px-2.5
                py-2
                text-xs
                font-bold
                sm:px-3
                sm:text-sm
              "
              style={{
                color: PRIMARY,
                background:
                  "rgba(14,165,233,0.08)",
              }}
            >
              <FaSignInAlt />

              <span className="hidden min-[400px]:inline">
                Login
              </span>
            </button>

            {/* SIGN UP */}

            <div className="relative shrink-0">

              <button
                type="button"
                onClick={() =>
                  setShowSignupMenu((prev) => !prev)
                }
                className="
                  flex
                  min-h-[38px]
                  items-center
                  justify-center
                  gap-1
                  rounded-xl
                  px-2.5
                  py-2
                  text-xs
                  font-bold
                  text-white
                  shadow-md
                  sm:gap-1.5
                  sm:px-3
                  sm:text-sm
                "
                style={{
                  background: BUTTON_GRADIENT,
                }}
              >
                <FaUserPlus />

                <span className="hidden min-[400px]:inline">
                  Sign Up
                </span>

                <FaChevronDown
                  className={`
                    text-[8px]
                    transition-transform
                    sm:text-[10px]
                    ${
                      showSignupMenu
                        ? "rotate-180"
                        : ""
                    }
                  `}
                />
              </button>

              {showSignupMenu && (
                <div
                  className="
                    absolute
                    right-0
                    top-full
                    z-[100]
                    mt-2
                    w-[min(15rem,calc(100vw-0.75rem))]
                    overflow-hidden
                    rounded-2xl
                    border
                    border-gray-100
                    bg-white
                    shadow-2xl
                  "
                >

                  {/* PATIENT */}

                  <button
                    type="button"
                    onClick={goToPatientSignup}
                    className="
                      flex
                      w-full
                      items-center
                      gap-3
                      px-3
                      py-3.5
                      text-left
                      transition
                      hover:bg-sky-50
                      sm:px-4
                      sm:py-4
                    "
                  >
                    <div
                      className="
                        flex
                        h-9
                        w-9
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        text-white
                        sm:h-10
                        sm:w-10
                        sm:rounded-xl
                      "
                      style={{
                        background: BUTTON_GRADIENT,
                      }}
                    >
                      <FaUserInjured />
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold text-gray-800 sm:text-sm">
                        Patient Sign Up
                      </p>

                      <p className="mt-0.5 text-[10px] text-gray-500 sm:text-xs">
                        Create patient account
                      </p>
                    </div>
                  </button>

                  <div className="mx-3 border-t border-gray-100 sm:mx-4" />

                  {/* PROFESSIONAL */}

                  <button
                    type="button"
                    onClick={goToDoctorSignup}
                    className="
                      flex
                      w-full
                      items-center
                      gap-3
                      px-3
                      py-3.5
                      text-left
                      transition
                      hover:bg-sky-50
                      sm:px-4
                      sm:py-4
                    "
                  >
                    <div
                      className="
                        flex
                        h-9
                        w-9
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        bg-emerald-100
                        text-emerald-600
                        sm:h-10
                        sm:w-10
                        sm:rounded-xl
                      "
                    >
                      <FaUserMd />
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold text-gray-800 sm:text-sm">
                        Doctor / Professional
                      </p>

                      <p className="mt-0.5 text-[10px] text-gray-500 sm:text-xs">
                        Register healthcare service
                      </p>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* =================================================================
          HERO
      ================================================================= */}

      <section
        className="
          relative
          w-full
          min-w-0
          overflow-hidden
        "
        style={{
          background:
            "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(255,255,255,1), rgba(30,73,118,0.08))",
        }}
      >

        {/* BACKGROUND DECORATION */}

        <div
          className="
            pointer-events-none
            absolute
            -left-20
            -top-20
            h-48
            w-48
            rounded-full
            blur-3xl
            sm:h-72
            sm:w-72
          "
          style={{
            background:
              "rgba(14,165,233,0.15)",
          }}
        />

        <div
          className="
            pointer-events-none
            absolute
            -right-24
            top-20
            h-64
            w-64
            rounded-full
            blur-3xl
            sm:h-96
            sm:w-96
          "
          style={{
            background:
              "rgba(30,73,118,0.15)",
          }}
        />

        <div
          className="
            pointer-events-none
            absolute
            bottom-0
            left-1/3
            h-48
            w-48
            rounded-full
            blur-3xl
            sm:h-64
            sm:w-64
          "
          style={{
            background:
              "rgba(26,58,92,0.10)",
          }}
        />

        <div
          className="
            relative
            mx-auto
            w-full
            min-w-0
            max-w-screen-2xl
            px-3
            py-10
            sm:px-5
            sm:py-14
            md:px-8
            md:py-16
            lg:px-10
            lg:py-20
            xl:px-12
          "
        >

          <div
            className="
              grid
              min-w-0
              items-center
              gap-10
              lg:grid-cols-2
              lg:gap-14
              xl:gap-16
            "
          >

            {/* ===========================================================
                HERO LEFT
            =========================================================== */}

            <div
              className="
                min-w-0
                text-center
                lg:text-left
              "
            >

              {/* BADGE */}

              <div
                className="
                  mb-5
                  inline-flex
                  max-w-full
                  items-center
                  gap-2
                  rounded-full
                  border
                  px-3
                  py-2
                  text-[10px]
                  font-bold
                  shadow-sm
                  sm:px-4
                  sm:text-sm
                "
                style={{
                  borderColor:
                    "rgba(14,165,233,0.25)",
                  background: "white",
                  color: PRIMARY,
                }}
              >
                <FaHeartbeat className="shrink-0 animate-pulse" />

                <span className="truncate">
                  Healthcare made simple
                </span>
              </div>

              {/* HEADING */}

              <h1
                className="
                  break-words
                  text-3xl
                  font-black
                  leading-[1.1]
                  tracking-tight
                  text-gray-900
                  min-[375px]:text-4xl
                  sm:text-5xl
                  md:text-6xl
                  lg:text-5xl
                  xl:text-7xl
                "
              >
                Your health.

                <span
                  className="
                    block
                    bg-clip-text
                    text-transparent
                  "
                  style={{
                    backgroundImage:
                      BUTTON_GRADIENT,
                  }}
                >
                  Our priority.
                </span>
              </h1>

              {/* DESCRIPTION */}

              <p
                className="
                  mx-auto
                  mt-5
                  max-w-2xl
                  text-sm
                  leading-6
                  text-gray-600
                  sm:mt-6
                  sm:text-lg
                  sm:leading-8
                  lg:mx-0
                  xl:text-xl
                "
              >
                MRHealth connects patients with doctors,
                hospitals, diagnostic centers, medical
                services and healthcare professionals —
                all in one place.
              </p>

              {/* =========================================================
                  SEARCH
              ========================================================= */}

              <div
                className="
                  relative
                  z-40
                  mx-auto
                  mt-7
                  w-full
                  min-w-0
                  max-w-3xl
                  lg:mx-0
                  sm:mt-8
                "
              >
                <div
                  className="
                    w-full
                    min-w-0
                    rounded-2xl
                    border
                    border-white/80
                    bg-white/90
                    p-2.5
                    shadow-2xl
                    shadow-blue-100
                    backdrop-blur-xl
                    sm:rounded-3xl
                    sm:p-4
                  "
                >

                  <div
                    className="
                      mb-2.5
                      flex
                      min-w-0
                      items-center
                      gap-2
                      px-1
                      sm:mb-3
                      sm:px-2
                    "
                  >
                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        text-sm
                        text-white
                        sm:h-9
                        sm:w-9
                        sm:rounded-xl
                      "
                      style={{
                        background:
                          BUTTON_GRADIENT,
                      }}
                    >
                      <FaSearch />
                    </div>

                    <div
                      className="
                        min-w-0
                        text-left
                      "
                    >
                      <p
                        className="
                          truncate
                          text-xs
                          font-extrabold
                          text-gray-900
                          sm:text-sm
                        "
                      >
                        Find healthcare near you
                      </p>

                      <p
                        className="
                          truncate
                          text-[10px]
                          text-gray-500
                          sm:text-xs
                        "
                      >
                        Search doctors, hospitals and healthcare services
                      </p>
                    </div>
                  </div>

                  {/* SEARCH BAR CONTAINER */}

                  <div className="w-full min-w-0 overflow-visible">
                    <SearchBar />
                  </div>
                </div>
              </div>

              {/* =========================================================
                  CTA
              ========================================================= */}

              <div
                className="
                  mt-5
                  flex
                  w-full
                  flex-col
                  gap-3
                  sm:mt-6
                  sm:flex-row
                  sm:justify-center
                  lg:justify-start
                "
              >

                <button
                  type="button"
                  onClick={() => navigate("/Home")}
                  className="
                    group
                    flex
                    min-h-[50px]
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    px-5
                    text-xs
                    font-bold
                    text-white
                    shadow-lg
                    transition
                    duration-200
                    hover:-translate-y-1
                    hover:shadow-xl
                    sm:w-auto
                    sm:px-6
                    sm:text-base
                  "
                  style={{
                    background:
                      BUTTON_GRADIENT,
                  }}
                >
                  <FaSearch />

                  <span>
                    Browse Healthcare
                  </span>

                  <FaArrowRight
                    className="
                      transition-transform
                      group-hover:translate-x-1
                    "
                  />
                </button>

                <button
                  type="button"
                  onClick={goToPatientSignup}
                  className="
                    flex
                    min-h-[50px]
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    border
                    bg-white
                    px-5
                    text-xs
                    font-bold
                    shadow-sm
                    transition
                    hover:-translate-y-1
                    sm:w-auto
                    sm:px-6
                    sm:text-base
                  "
                  style={{
                    borderColor:
                      "rgba(26,58,92,0.25)",
                    color: PRIMARY,
                  }}
                >
                  <FaUserInjured />

                  <span>
                    Patient Sign Up
                  </span>
                </button>
              </div>

              {/* =========================================================
                  TRUST
              ========================================================= */}

              <div
                className="
                  mt-6
                  flex
                  flex-wrap
                  justify-center
                  gap-x-4
                  gap-y-2.5
                  text-[10px]
                  text-gray-600
                  sm:mt-7
                  sm:gap-x-5
                  sm:text-sm
                  lg:justify-start
                "
              >

                <span className="flex items-center gap-1.5">
                  <FaCheckCircle className="shrink-0 text-green-500" />
                  Easy search
                </span>

                <span className="flex items-center gap-1.5">
                  <FaCheckCircle className="shrink-0 text-green-500" />
                  Nearby services
                </span>

                <span className="flex items-center gap-1.5">
                  <FaCheckCircle className="shrink-0 text-green-500" />
                  Secure platform
                </span>

              </div>
            </div>

            {/* ===========================================================
                HERO RIGHT
            =========================================================== */}

            <div
              className="
                relative
                mx-auto
                w-full
                min-w-0
                max-w-xl
              "
            >
              <div
                className="
                  relative
                  w-full
                  min-w-0
                  rounded-2xl
                  border
                  border-white/70
                  bg-white/80
                  p-3.5
                  shadow-2xl
                  shadow-blue-100
                  backdrop-blur-xl
                  sm:rounded-3xl
                  sm:p-5
                  md:p-7
                "
              >

                {/* HEADER */}

                <div
                  className="
                    flex
                    min-w-0
                    items-center
                    justify-between
                    gap-3
                    border-b
                    border-gray-100
                    pb-4
                    sm:pb-5
                  "
                >

                  <div className="min-w-0 flex-1">

                    <p
                      className="
                        text-[10px]
                        font-semibold
                        uppercase
                        tracking-wider
                        sm:text-xs
                      "
                      style={{
                        color: PRIMARY_LIGHT,
                      }}
                    >
                      MRHealth
                    </p>

                    <h2
                      className="
                        mt-1
                        break-words
                        text-base
                        font-extrabold
                        leading-tight
                        text-gray-900
                        sm:text-xl
                        md:text-2xl
                      "
                    >
                      Healthcare at your fingertips
                    </h2>

                  </div>

                  <div
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      text-base
                      text-white
                      sm:h-12
                      sm:w-12
                      sm:rounded-2xl
                      sm:text-xl
                    "
                    style={{
                      background:
                        BUTTON_GRADIENT,
                    }}
                  >
                    <FaHeartbeat />
                  </div>
                </div>

                {/* SEARCH INFORMATION */}

                <div
                  className="
                    mt-4
                    w-full
                    min-w-0
                    rounded-xl
                    p-2.5
                    sm:mt-5
                    sm:rounded-2xl
                    sm:p-4
                  "
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(30,73,118,0.08))",
                  }}
                >

                  <div
                    className="
                      flex
                      min-w-0
                      items-center
                      gap-2
                      rounded-xl
                      bg-white
                      p-2.5
                      shadow-sm
                      sm:gap-3
                      sm:p-4
                    "
                  >

                    <div
                      className="
                        flex
                        h-9
                        w-9
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        text-sm
                        text-white
                        sm:h-10
                        sm:w-10
                        sm:rounded-xl
                      "
                      style={{
                        background:
                          BUTTON_GRADIENT,
                      }}
                    >
                      <FaSearch />
                    </div>

                    <div className="min-w-0 flex-1">

                      <p className="truncate text-xs font-bold text-gray-800 sm:text-sm">
                        Search healthcare
                      </p>

                      <p className="mt-1 truncate text-[10px] text-gray-400 sm:text-xs">
                        Doctors • Hospitals • Diagnostics • Pharmacy
                      </p>

                    </div>
                  </div>

                  <div
                    className="
                      mt-2
                      flex
                      min-w-0
                      items-start
                      gap-2
                      text-[10px]
                      leading-5
                      sm:mt-3
                      sm:text-xs
                    "
                    style={{
                      color: PRIMARY_LIGHT,
                    }}
                  >
                    <FaMapMarkerAlt className="mt-1 shrink-0" />

                    <span className="break-words">
                      Find healthcare services near you
                    </span>
                  </div>
                </div>

                {/* SERVICES */}

                <div
                  className="
                    mt-4
                    grid
                    grid-cols-2
                    gap-2
                    sm:mt-5
                    sm:grid-cols-3
                    sm:gap-3
                  "
                >
                  {[
                    {
                      icon: <FaUserMd />,
                      title: "Doctors",
                      text: "Specialists",
                    },
                    {
                      icon: <MdLocalHospital />,
                      title: "Hospitals",
                      text: "Healthcare",
                    },
                    {
                      icon: <FaFlask />,
                      title: "Diagnostics",
                      text: "Labs & tests",
                    },
                    {
                      icon: <FaCapsules />,
                      title: "Pharmacy",
                      text: "Medical shops",
                    },
                    {
                      icon: <FaAmbulance />,
                      title: "Ambulance",
                      text: "Emergency",
                    },
                    {
                      icon: <FaLeaf />,
                      title: "Wellness",
                      text: "Alternative",
                    },
                  ].map((service) => (
                    <button
                      type="button"
                      key={service.title}
                      onClick={() => navigate("/search")}
                      className="
                        group
                        min-w-0
                        rounded-xl
                        p-2.5
                        text-left
                        transition
                        duration-200
                        hover:-translate-y-1
                        sm:rounded-2xl
                        sm:p-4
                      "
                      style={{
                        background:
                          "rgba(14,165,233,0.07)",
                      }}
                    >

                      <div
                        className="
                          text-lg
                          sm:text-xl
                        "
                        style={{
                          color: PRIMARY_LIGHT,
                        }}
                      >
                        {service.icon}
                      </div>

                      <p
                        className="
                          mt-1.5
                          truncate
                          text-xs
                          font-bold
                          text-gray-800
                          sm:mt-2
                          sm:text-sm
                        "
                      >
                        {service.title}
                      </p>

                      <p
                        className="
                          mt-1
                          truncate
                          text-[9px]
                          text-gray-500
                          sm:text-[11px]
                        "
                      >
                        {service.text}
                      </p>
                    </button>
                  ))}
                </div>

                {/* FLOATING MESSAGE */}

                <div
                  className="
                    absolute
                    -right-2
                    top-24
                    hidden
                    rounded-2xl
                    border
                    border-gray-100
                    bg-white
                    p-3
                    shadow-xl
                    sm:block
                    md:-right-6
                  "
                >
                  <div className="flex items-center gap-3">

                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        text-white
                      "
                      style={{
                        background:
                          BUTTON_GRADIENT,
                      }}
                    >
                      <FaCheckCircle />
                    </div>

                    <div className="min-w-0">

                      <p className="text-xs font-bold text-gray-800">
                        Healthcare simplified
                      </p>

                      <p className="text-[10px] text-gray-400">
                        Search • Connect • Manage
                      </p>

                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================================================================
          PATIENT / PROFESSIONAL
      ================================================================= */}

      <section
        className="
          w-full
          min-w-0
          bg-white
          py-12
          sm:py-16
          lg:py-20
        "
      >
        <div
          className="
            mx-auto
            w-full
            min-w-0
            max-w-screen-2xl
            px-3
            sm:px-5
            md:px-8
            lg:px-10
            xl:px-12
          "
        >

          <div className="mx-auto max-w-3xl text-center">

            <span
              className="
                text-[10px]
                font-bold
                uppercase
                tracking-widest
                sm:text-xs
              "
              style={{
                color: SKY,
              }}
            >
              One platform
            </span>

            <h2
              className="
                mt-2
                text-2xl
                font-black
                text-gray-900
                sm:text-4xl
              "
            >
              Healthcare for everyone
            </h2>

            <p
              className="
                mt-3
                text-xs
                leading-6
                text-gray-500
                sm:mt-4
                sm:text-base
                sm:leading-7
              "
            >
              Whether you need healthcare or provide healthcare,
              MRHealth connects you with the right people and services.
            </p>
          </div>

          <div
            className="
              mx-auto
              mt-8
              grid
              w-full
              max-w-5xl
              gap-4
              md:mt-10
              md:grid-cols-2
              md:gap-5
            "
          >

            {/* PATIENT */}

            <div
              className="
                group
                relative
                min-w-0
                overflow-hidden
                rounded-2xl
                border
                p-5
                shadow-sm
                transition
                duration-300
                hover:-translate-y-1
                hover:shadow-xl
                sm:rounded-3xl
                sm:p-8
              "
              style={{
                borderColor:
                  "rgba(14,165,233,0.20)",
                background:
                  "linear-gradient(135deg, rgba(14,165,233,0.08), white)",
              }}
            >

              <div
                className="
                  absolute
                  -right-16
                  -top-16
                  h-40
                  w-40
                  rounded-full
                  transition
                  group-hover:scale-125
                "
                style={{
                  background:
                    "rgba(14,165,233,0.10)",
                }}
              />

              <div className="relative">

                <div
                  className="
                    flex
                    h-12
                    w-12
                    items-center
                    justify-center
                    rounded-xl
                    text-xl
                    text-white
                    shadow-lg
                    sm:h-14
                    sm:w-14
                    sm:rounded-2xl
                    sm:text-2xl
                  "
                  style={{
                    background:
                      BUTTON_GRADIENT,
                  }}
                >
                  <FaUserInjured />
                </div>

                <h3
                  className="
                    mt-4
                    text-xl
                    font-extrabold
                    text-gray-900
                    sm:mt-5
                    sm:text-2xl
                  "
                >
                  I am a Patient
                </h3>

                <p
                  className="
                    mt-2
                    text-xs
                    leading-6
                    text-gray-600
                    sm:mt-3
                    sm:text-sm
                    sm:leading-7
                  "
                >
                  Find doctors, hospitals, diagnostic centers,
                  medical shops and healthcare services near you.
                </p>

                <div
                  className="
                    mt-5
                    space-y-2.5
                    text-xs
                    text-gray-600
                    sm:mt-6
                    sm:space-y-3
                    sm:text-sm
                  "
                >
                  {[
                    "Search healthcare professionals",
                    "Find nearby healthcare services",
                    "Manage your healthcare journey",
                  ].map((text) => (
                    <div
                      key={text}
                      className="
                        flex
                        items-start
                        gap-2
                      "
                    >
                      <FaCheckCircle className="mt-0.5 shrink-0 text-green-500" />

                      <span>
                        {text}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={goToPatientSignup}
                  className="
                    mt-6
                    flex
                    min-h-[48px]
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    px-5
                    text-xs
                    font-bold
                    text-white
                    transition
                    hover:opacity-90
                    sm:mt-7
                    sm:text-sm
                  "
                  style={{
                    background:
                      BUTTON_GRADIENT,
                  }}
                >
                  <FaUserPlus />
                  Patient Sign Up
                  <FaArrowRight />
                </button>
              </div>
            </div>

            {/* PROFESSIONAL */}

            <div
              className="
                group
                relative
                min-w-0
                overflow-hidden
                rounded-2xl
                border
                border-emerald-100
                bg-gradient-to-br
                from-emerald-50
                to-white
                p-5
                shadow-sm
                transition
                duration-300
                hover:-translate-y-1
                hover:shadow-xl
                sm:rounded-3xl
                sm:p-8
              "
            >

              <div
                className="
                  absolute
                  -right-16
                  -top-16
                  h-40
                  w-40
                  rounded-full
                  bg-emerald-100/60
                  transition
                  group-hover:scale-125
                "
              />

              <div className="relative">

                <div
                  className="
                    flex
                    h-12
                    w-12
                    items-center
                    justify-center
                    rounded-xl
                    bg-emerald-600
                    text-xl
                    text-white
                    shadow-lg
                    sm:h-14
                    sm:w-14
                    sm:rounded-2xl
                    sm:text-2xl
                  "
                >
                  <FaUserMd />
                </div>

                <h3
                  className="
                    mt-4
                    break-words
                    text-xl
                    font-extrabold
                    text-gray-900
                    sm:mt-5
                    sm:text-2xl
                  "
                >
                  Healthcare Professional
                </h3>

                <p
                  className="
                    mt-2
                    text-xs
                    leading-6
                    text-gray-600
                    sm:mt-3
                    sm:text-sm
                    sm:leading-7
                  "
                >
                  Register your healthcare service, manage your
                  profile, availability and connect with patients.
                </p>

                <div
                  className="
                    mt-5
                    space-y-2.5
                    text-xs
                    text-gray-600
                    sm:mt-6
                    sm:space-y-3
                    sm:text-sm
                  "
                >
                  {[
                    "Create professional profile",
                    "Manage availability",
                    "Connect with patients",
                  ].map((text) => (
                    <div
                      key={text}
                      className="
                        flex
                        items-start
                        gap-2
                      "
                    >
                      <FaCheckCircle className="mt-0.5 shrink-0 text-green-500" />

                      <span>
                        {text}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={goToDoctorSignup}
                  className="
                    mt-6
                    flex
                    min-h-[48px]
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    px-5
                    text-xs
                    font-bold
                    text-white
                    transition
                    hover:opacity-90
                    sm:mt-7
                    sm:text-sm
                  "
                  style={{
                    background:
                      "linear-gradient(135deg, rgb(5, 150, 105), rgb(16, 185, 129))",
                  }}
                >
                  <FaStethoscope />

                  <span>
                    Professional Registration
                  </span>

                  <FaArrowRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================================================================
          HOW IT WORKS
      ================================================================= */}

      <section
        className="
          w-full
          min-w-0
          py-12
          sm:py-16
          lg:py-20
        "
        style={{
          background:
            "linear-gradient(to bottom, rgba(15,31,51,0.035), white)",
        }}
      >
        <div
          className="
            mx-auto
            w-full
            min-w-0
            max-w-screen-2xl
            px-3
            sm:px-5
            md:px-8
            lg:px-10
            xl:px-12
          "
        >

          <div className="text-center">

            <span
              className="
                text-[10px]
                font-bold
                uppercase
                tracking-widest
                sm:text-xs
              "
              style={{
                color: SKY,
              }}
            >
              Simple process
            </span>

            <h2
              className="
                mt-2
                text-2xl
                font-black
                text-gray-900
                sm:text-4xl
              "
            >
              Healthcare in three simple steps
            </h2>
          </div>

          <div
            className="
              mx-auto
              mt-9
              grid
              max-w-5xl
              gap-8
              md:mt-12
              md:grid-cols-3
            "
          >
            {steps.map((step) => (
              <div
                key={step.number}
                className="
                  min-w-0
                  text-center
                "
              >

                <div
                  className="
                    mx-auto
                    flex
                    h-14
                    w-14
                    items-center
                    justify-center
                    rounded-xl
                    text-lg
                    text-white
                    shadow-lg
                    sm:h-16
                    sm:w-16
                    sm:rounded-2xl
                    sm:text-xl
                  "
                  style={{
                    background:
                      BUTTON_GRADIENT,
                  }}
                >
                  {step.icon}
                </div>

                <div
                  className="
                    mt-4
                    text-[10px]
                    font-black
                    tracking-widest
                    sm:mt-5
                    sm:text-xs
                  "
                  style={{
                    color: SKY,
                  }}
                >
                  STEP {step.number}
                </div>

                <h3
                  className="
                    mt-2
                    text-lg
                    font-extrabold
                    text-gray-900
                    sm:text-xl
                  "
                >
                  {step.title}
                </h3>

                <p
                  className="
                    mx-auto
                    mt-2
                    max-w-xs
                    text-xs
                    leading-6
                    text-gray-500
                    sm:mt-3
                    sm:text-sm
                  "
                >
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================================
          FEATURES
      ================================================================= */}

      <section
        className="
          w-full
          min-w-0
          bg-white
          py-12
          sm:py-16
          lg:py-20
        "
      >
        <div
          className="
            mx-auto
            w-full
            min-w-0
            max-w-screen-2xl
            px-3
            sm:px-5
            md:px-8
            lg:px-10
            xl:px-12
          "
        >

          <div
            className="
              grid
              min-w-0
              gap-8
              lg:grid-cols-[0.8fr_1.2fr]
              lg:items-center
            "
          >

            <div className="min-w-0">

              <span
                className="
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-widest
                  sm:text-xs
                "
                style={{
                  color: SKY,
                }}
              >
                Why MRHealth
              </span>

              <h2
                className="
                  mt-2
                  break-words
                  text-2xl
                  font-black
                  leading-tight
                  text-gray-900
                  sm:mt-3
                  sm:text-4xl
                "
              >
                Everything you need for a simpler healthcare experience.
              </h2>

              <p
                className="
                  mt-4
                  max-w-lg
                  text-xs
                  leading-6
                  text-gray-500
                  sm:mt-5
                  sm:text-base
                  sm:leading-7
                "
              >
                MRHealth brings multiple healthcare services together
                so patients can search, discover and connect with
                healthcare professionals conveniently.
              </p>

              <button
                type="button"
                onClick={() => navigate("/Home")}
                className="
                  mt-6
                  inline-flex
                  min-h-[46px]
                  items-center
                  gap-2
                  rounded-xl
                  px-4
                  text-xs
                  font-bold
                  text-white
                  transition
                  hover:opacity-90
                  sm:mt-7
                  sm:px-5
                  sm:text-sm
                "
                style={{
                  background:
                    CUSTOM_GRADIENT,
                }}
              >
                Explore Healthcare
                <FaArrowRight />
              </button>
            </div>

            <div
              className="
                grid
                min-w-0
                gap-3
                sm:grid-cols-2
                sm:gap-4
              "
            >
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="
                    group
                    min-w-0
                    rounded-2xl
                    border
                    border-gray-100
                    bg-white
                    p-4
                    shadow-sm
                    transition
                    duration-300
                    hover:-translate-y-1
                    hover:shadow-lg
                    sm:p-5
                  "
                >

                  <div
                    className="
                      flex
                      h-11
                      w-11
                      items-center
                      justify-center
                      rounded-xl
                      text-base
                      text-white
                      transition
                      sm:h-12
                      sm:w-12
                      sm:text-lg
                    "
                    style={{
                      background:
                        BUTTON_GRADIENT,
                    }}
                  >
                    {feature.icon}
                  </div>

                  <h3
                    className="
                      mt-3
                      text-sm
                      font-extrabold
                      text-gray-900
                      sm:mt-4
                      sm:text-base
                    "
                  >
                    {feature.title}
                  </h3>

                  <p
                    className="
                      mt-1.5
                      text-xs
                      leading-5
                      text-gray-500
                      sm:mt-2
                      sm:text-sm
                      sm:leading-6
                    "
                  >
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================================================================
          SERVICES
      ================================================================= */}

      <section
        className="
          w-full
          min-w-0
          bg-gray-50
          py-12
          sm:py-16
          lg:py-20
        "
      >
        <div
          className="
            mx-auto
            w-full
            min-w-0
            max-w-screen-2xl
            px-3
            sm:px-5
            md:px-8
            lg:px-10
            xl:px-12
          "
        >

          <div
            className="
              flex
              min-w-0
              flex-col
              justify-between
              gap-3
              sm:flex-row
              sm:items-end
              sm:gap-4
            "
          >

            <div className="min-w-0">

              <span
                className="
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-widest
                  sm:text-xs
                "
                style={{
                  color: SKY,
                }}
              >
                Explore
              </span>

              <h2
                className="
                  mt-2
                  text-2xl
                  font-black
                  text-gray-900
                  sm:text-4xl
                "
              >
                Healthcare services
              </h2>

              <p
                className="
                  mt-2
                  max-w-xl
                  text-xs
                  leading-6
                  text-gray-500
                  sm:mt-3
                  sm:text-base
                "
              >
                Explore different healthcare categories available
                through MRHealth.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/search")}
              className="
                inline-flex
                w-fit
                shrink-0
                items-center
                gap-2
                text-xs
                font-bold
                sm:text-sm
              "
              style={{
                color: PRIMARY,
              }}
            >
              View all
              <FaArrowRight />
            </button>
          </div>

          <div
            className="
              mt-7
              grid
              grid-cols-1
              gap-3
              sm:grid-cols-2
              sm:gap-4
              lg:grid-cols-3
              xl:grid-cols-6
            "
          >
            {specialties.map((item) => (
              <button
                type="button"
                key={item.title}
                onClick={() => navigate("/search")}
                className="
                  group
                  min-w-0
                  rounded-2xl
                  border
                  border-gray-100
                  bg-white
                  p-4
                  text-left
                  shadow-sm
                  transition
                  duration-300
                  hover:-translate-y-1
                  hover:shadow-lg
                  sm:p-5
                "
              >

                <div
                  className="
                    flex
                    h-11
                    w-11
                    items-center
                    justify-center
                    rounded-xl
                    text-lg
                    text-white
                    transition
                    sm:h-12
                    sm:w-12
                    sm:text-xl
                  "
                  style={{
                    background:
                      BUTTON_GRADIENT,
                  }}
                >
                  {item.icon}
                </div>

                <h3
                  className="
                    mt-3
                    truncate
                    text-sm
                    font-extrabold
                    text-gray-900
                    sm:mt-4
                    sm:text-base
                  "
                >
                  {item.title}
                </h3>

                <p
                  className="
                    mt-1.5
                    line-clamp-3
                    text-[11px]
                    leading-5
                    text-gray-500
                    sm:mt-2
                    sm:text-xs
                  "
                >
                  {item.description}
                </p>

                <div
                  className="
                    mt-3
                    flex
                    items-center
                    gap-1
                    text-[11px]
                    font-bold
                    sm:mt-4
                    sm:text-xs
                  "
                  style={{
                    color: PRIMARY,
                  }}
                >
                  Explore

                  <FaArrowRight
                    className="
                      transition-transform
                      group-hover:translate-x-1
                    "
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================================
          CTA
      ================================================================= */}

      <section
        className="
          relative
          w-full
          min-w-0
          overflow-hidden
          py-12
          sm:py-16
          lg:py-20
        "
        style={{
          background:
            CUSTOM_GRADIENT,
        }}
      >

        <div
          className="
            pointer-events-none
            absolute
            -left-20
            -top-20
            h-48
            w-48
            rounded-full
            bg-white/10
            sm:h-64
            sm:w-64
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            -bottom-24
            -right-16
            h-64
            w-64
            rounded-full
            bg-white/10
            sm:h-80
            sm:w-80
          "
        />

        <div
          className="
            relative
            mx-auto
            w-full
            max-w-4xl
            px-3
            text-center
            sm:px-6
          "
        >

          <div
            className="
              mx-auto
              flex
              h-14
              w-14
              items-center
              justify-center
              rounded-xl
              bg-white/15
              text-xl
              text-white
              backdrop-blur
              sm:h-16
              sm:w-16
              sm:rounded-2xl
              sm:text-2xl
            "
          >
            <MdHealthAndSafety />
          </div>

          <h2
            className="
              mt-5
              break-words
              text-2xl
              font-black
              leading-tight
              text-white
              sm:mt-6
              sm:text-4xl
              lg:text-5xl
            "
          >
            Take the next step toward better healthcare
          </h2>

          <p
            className="
              mx-auto
              mt-4
              max-w-2xl
              text-xs
              leading-6
              text-blue-50
              sm:mt-5
              sm:text-base
              sm:leading-7
            "
          >
            Find the healthcare services you need or register
            your healthcare service with MRHealth.
          </p>

          <div
            className="
              mt-6
              flex
              flex-col
              justify-center
              gap-3
              sm:mt-8
              sm:flex-row
            "
          >

            <button
              type="button"
              onClick={() => navigate("/Home")}
              className="
                flex
                min-h-[48px]
                w-full
                items-center
                justify-center
                gap-2
                rounded-xl
                bg-white
                px-5
                text-xs
                font-bold
                shadow-lg
                transition
                hover:bg-gray-100
                sm:w-auto
                sm:px-6
                sm:text-base
              "
              style={{
                color: PRIMARY,
              }}
            >
              <FaSearch />
              Find Healthcare
            </button>

            <button
              type="button"
              onClick={goToDoctorSignup}
              className="
                flex
                min-h-[48px]
                w-full
                items-center
                justify-center
                gap-2
                rounded-xl
                border
                border-white/40
                bg-white/10
                px-5
                text-xs
                font-bold
                text-white
                backdrop-blur
                transition
                hover:bg-white
                sm:w-auto
                sm:px-6
                sm:text-base
              "
              onMouseEnter={(e) => {
                e.currentTarget.style.color =
                  PRIMARY;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color =
                  "white";
              }}
            >
              <FaStethoscope />

              <span>
                Professional Registration
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* =================================================================
          FOOTER
      ================================================================= */}

      <footer
        className="
          w-full
          min-w-0
          bg-gray-950
          px-3
          py-7
          text-gray-400
          sm:px-6
          sm:py-8
        "
      >
        <div
          className="
            mx-auto
            flex
            w-full
            max-w-screen-2xl
            flex-col
            items-center
            justify-between
            gap-5
            text-center
            sm:flex-row
            sm:text-left
          "
        >

          <div className="min-w-0">

            <p className="text-base font-extrabold text-white">
              MRHealth
            </p>

            <p className="mt-1 text-[10px] sm:text-xs">
              Connecting patients with healthcare services.
            </p>
          </div>

          <div
            className="
              flex
              max-w-full
              flex-wrap
              items-center
              justify-center
              gap-1.5
              text-[10px]
              sm:gap-4
              sm:text-xs
            "
          >

            <Link
              to="/Home"
              className="
                flex
                items-center
                gap-1.5
                rounded-xl
                px-3
                py-2
                text-xs
                font-bold
                text-white
                transition
                hover:opacity-90
                sm:px-4
                sm:text-sm
              "
              style={{
                background:
                  BUTTON_GRADIENT,
              }}
            >
              <FaHome />
              Home
            </Link>

            <Link
              to="/about"
              className="transition hover:text-white"
            >
              About
            </Link>

            <Link
              to="/legal-policies"
              className="transition hover:text-white"
            >
              Policies
            </Link>

            <Link
              to="/login"
              className="transition hover:text-white"
            >
              Login
            </Link>

            <button
              type="button"
              onClick={goToPatientSignup}
              className="transition hover:text-white"
            >
              Patient Sign Up
            </button>

            <button
              type="button"
              onClick={goToDoctorSignup}
              className="transition hover:text-white"
            >
              Professional Registration
            </button>
          </div>
        </div>

        <div
          className="
            mx-auto
            mt-5
            w-full
            max-w-screen-2xl
            border-t
            border-white/10
            pt-4
            text-center
            text-[10px]
            text-gray-500
            sm:mt-6
            sm:pt-5
            sm:text-xs
          "
        >
          © {new Date().getFullYear()} MRHealth · mrhealth.co.in
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;